import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { NAV } from "@/constants/testIds";
import { Logo } from "@/components/Logo";
import { getMarket, fmt } from "@/config/markets";
import {
  Ticket, ArrowRight, ArrowUpRight, Skull, HandCoins, Lock, EyeOff,
  Handshake, Sparkles, PenLine,
} from "lucide-react";

/**
 * Detect visitor's active market. Reads the sticky selection saved by Landing.jsx
 * when they visited /uk or /us. Defaults to UK unless we can confirm US.
 */
function useAboutMarket() {
  return useMemo(() => {
    try {
      const saved = localStorage.getItem("st_market");
      if (saved === "us") return "us";
    } catch (e) { /* ignore */ }
    return "uk";
  }, []);
}

const painPointsFor = (market) => ([
  {
    id: "greedy-fees",
    icon: HandCoins,
    label: "Time to end ticket tax",
    title: "Platforms charge more than the venue makes.",
    body: `A 10% platform fee on a ${fmt(market, 15)} ticket is ${fmt(market, 1.5, { decimals: 2 })}. A card fee on top. Then a “service fee”. Then a “buyer fee”. Somewhere between the poster and the front door, someone else is quietly earning more per ticket than the venue that booked the act, paid the sound guy, and turned the lights on.`,
  },
  {
    id: "hostage-money",
    icon: Lock,
    label: "Held hostage",
    title: "Your money, on their schedule.",
    body: "Most platforms hold your revenue until after the event — sometimes weeks after. So the venue that took the risk, paid the deposit, and printed the flyers is the one lending money to the ticketing company. That's not a platform. That's a payday lender in a hoodie.",
  },
  {
    id: "data-hostage",
    icon: EyeOff,
    label: "You don't own your own customers",
    title: "The buyers are theirs, not yours.",
    body: "Every ticket sold is a first-party relationship — and most platforms take it for themselves. Suddenly your regulars are getting emails about the venue across town. You built the community. They monetise it. Then they sell you ads to reach it.",
  },
  {
    id: "ugly-checkout",
    icon: Skull,
    label: "Branded to death",
    title: "Their logo. Their pop-ups. Their upsells.",
    body: "The moment a buyer hits “Buy Tickets”, your brand disappears. Cross-sells for other venues. Prompts to “download the app”. Confirmation emails from a stranger. It's the digital equivalent of a bouncer swapping your wristband for theirs.",
  },
]);

const beliefs = [
  {
    n: "01",
    title: "Venues do the hard bit.",
    body: "You take the booking risk. You pay the rent, the staff, the licences. A ticketing platform is a piece of software — and it should never charge you for the privilege of running your own events. That's why we don't.",
  },
  {
    n: "02",
    title: "100% of the ticket is yours.",
    body: "Every penny of face value lands in your Stripe. Buyers pay a small booking fee per ticket that covers card processing — not you. You keep 100% of your list price on every ticket you sell (up to £100 per ticket). And as a Founding Partner, we rebate £0.25 back on every ticket up to £75 for your first 2,000 sold.",
  },
  {
    n: "03",
    title: "Payouts move on Stripe's clock, not ours.",
    body: "We never touch your money. It goes straight from the buyer to your Stripe account, on Stripe's normal payout schedule. No “event hold”. No “risk review”. No excuses.",
  },
  {
    n: "04",
    title: "The customer is yours. Full stop.",
    body: "Every buyer's email, name and order history belongs to the venue that sold them the ticket. We don't remarket to your fans. We don't sell them events across town. We don't build a lookalike audience off your back.",
  },
  {
    n: "05",
    title: "Your brand, edge to edge.",
    body: "Your logo, your colours, your domain, your emails. Buyers should never see our name — and by default, they don't. If we did our job right, they think you built the whole thing yourself.",
  },
];

export default function About() {
  const marketKey = useAboutMarket();
  const market = getMarket(marketKey);
  const painPoints = painPointsFor(market);
  // Rewrite belief #02 with market-aware currency so the site keeps the same tone across UK/US
  const beliefsLocal = beliefs.map((b) => (
    b.n === "02"
      ? { ...b, body: `Every penny of face value lands in your Stripe. Buyers pay a small booking fee at checkout — ${Math.round(market.bookingFeePercent * 100)}% of the ticket price with a ${fmt(market, market.bookingFeeMinimum, { decimals: 2 })} minimum. That's how we get paid. You keep 100% of your list price on every ticket you sell — no card fees, no monthly bill, no ticket price cap.` }
      : b
  ));
  const s = market.savings;

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="about-page">
      {/* Nav */}
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-32 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center"
            data-testid={NAV.logo}
          >
            <Logo size="h-28" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
            <Link to="/#features" className="text-zinc-700 hover:text-zinc-900">Features</Link>
            <Link to="/#pricing" className="text-zinc-700 hover:text-zinc-900">Pricing</Link>
            <Link to="/about" className="text-zinc-900 underline decoration-indigo-600 underline-offset-4 decoration-2">About</Link>
            <Link to="/#faq" className="text-zinc-700 hover:text-zinc-900">FAQ</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" data-testid={NAV.loginBtn} className="rounded-full">Log in</Button>
            </Link>
            <Link to="/register">
              <Button data-testid={NAV.signupBtn} className="rounded-full bg-zinc-900 hover:bg-zinc-800">
                Start selling <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-200">
        <div className="absolute inset-0 grid-bg opacity-40" aria-hidden="true" />
        <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-20 relative">
          <div className="text-sm font-mono text-indigo-600 uppercase tracking-widest">/ about</div>
          <h1 className="mt-4 font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter max-w-4xl">
            We built Affra because <span className="text-indigo-600">no ticketing company</span> should out-earn the venue.
          </h1>
          <p className="mt-8 text-xl text-zinc-600 max-w-2xl leading-relaxed">
            You take the risk. You book the acts. You pay the staff. You keep the lights on. And then, somewhere between the poster and the door, a platform quietly clips more off every ticket than you do.
          </p>
          <p className="mt-4 text-xl text-zinc-900 font-semibold max-w-2xl leading-relaxed">
            We think that&apos;s backwards. So we flipped it.
          </p>
        </div>
      </section>

      {/* THE PAIN */}
      <section className="relative bg-zinc-900 text-white overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-[0.04]" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 relative">
          <div className="max-w-3xl mb-16">
            <div className="text-sm font-mono text-indigo-400 uppercase tracking-widest">/ the industry today</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-6xl tracking-tighter leading-none">
              Four things venues quietly hate about ticketing platforms.
            </h2>
            <p className="mt-6 text-zinc-400 text-lg max-w-2xl">
              None of these are secrets. They&apos;re just so normalised that most operators have stopped asking why.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
            {painPoints.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.id}
                  data-testid={`pain-${p.id}`}
                  className="bg-zinc-900 p-8 md:p-10"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 grid place-items-center bg-white/5 border border-white/10 rounded-lg">
                      <Icon className="w-5 h-5 text-indigo-400" aria-hidden="true" />
                    </span>
                    <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">{p.label}</span>
                  </div>
                  <h3 className="mt-6 font-display font-bold text-2xl tracking-tight leading-tight">{p.title}</h3>
                  <p className="mt-3 text-zinc-400 text-[15px] leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-16 max-w-3xl">
            <p className="font-display font-black text-3xl lg:text-4xl tracking-tighter leading-tight">
              Add it up and it&apos;s the same story: <span className="text-indigo-400">someone else is getting rich off your room.</span>
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE BELIEVE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="max-w-3xl mb-16">
          <div className="text-sm font-mono text-indigo-600 uppercase tracking-widest">/ what we believe</div>
          <h2 className="mt-3 font-display font-black text-4xl lg:text-6xl tracking-tighter leading-none">
            Your success is our success. Not our exit.
          </h2>
          <p className="mt-6 text-zinc-600 text-lg max-w-2xl">
            Affra is a flat-fee SaaS, not a percentage-of-your-life SaaS. If your ticket sales double, our bill to you stays exactly the same.
          </p>
        </div>

        <ol className="divide-y divide-zinc-200 border-y border-zinc-200">
          {beliefsLocal.map((b) => (
            <li
              key={b.n}
              data-testid={`belief-${b.n}`}
              className="grid md:grid-cols-12 gap-6 md:gap-10 py-10 group"
            >
              <div className="md:col-span-2">
                <span className="font-mono text-sm text-indigo-600">/ {b.n}</span>
              </div>
              <div className="md:col-span-10">
                <h3 className="font-display font-black text-2xl lg:text-4xl tracking-tighter">
                  {b.title}
                </h3>
                <p className="mt-3 text-zinc-600 text-[17px] leading-relaxed max-w-3xl">{b.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* MATH SECTION */}
      <section className="border-y border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="text-sm font-mono text-indigo-600 uppercase tracking-widest">/ the maths</div>
              <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tighter leading-[0.95]">
                On 1,000 tickets at {fmt(market, s.ticketPrice)} a pop, we&apos;re the only one that leaves the venue better off.
              </h2>
              <p className="mt-6 text-zinc-600 text-lg max-w-md">
                Same event. Same tickets. Very different bank balance at the end of the month.
              </p>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              <div className="rounded-xl bg-white border border-zinc-200 p-6">
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Big-name platform</div>
                <div className="mt-4 font-display font-black text-4xl tracking-tighter">{fmt(market, s.competitorNet)}</div>
                <div className="mt-1 text-sm text-zinc-500">to the venue</div>
                <ul className="mt-6 text-sm text-zinc-600 space-y-1.5">
                  <li>– 10% platform fee</li>
                  <li>– Card processing {s.competitorCardLabel}</li>
                  <li>– Payout held until after event</li>
                  <li>– Buyer data goes to platform</li>
                </ul>
              </div>
              <div className="rounded-xl bg-zinc-900 text-white p-6 relative overflow-hidden border border-zinc-900">
                <div className="absolute top-3 right-3 text-[10px] font-mono uppercase tracking-widest bg-indigo-600 text-white px-2 py-0.5 rounded-full">
                  Affra
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-indigo-400">Us</div>
                <div className="mt-4 font-display font-black text-4xl tracking-tighter">{fmt(market, s.ourNet)}</div>
                <div className="mt-1 text-sm text-zinc-400">to the venue — no fees, ever</div>
                <ul className="mt-6 text-sm text-zinc-300 space-y-1.5">
                  <li>+ 100% of every ticket price to you</li>
                  <li>+ We absorb Stripe card processing on every sale</li>
                  <li>+ {Math.round(market.bookingFeePercent * 100)}% booking fee ({fmt(market, market.bookingFeeMinimum, { decimals: 2 })} min) — paid by the buyer</li>
                  <li>+ Buyer pays the booking fee, not you</li>
                  <li>+ {fmt(market, 0)} monthly fees, {fmt(market, 0)} per-ticket cost to you</li>
                  <li>+ Money lands in your Stripe, straight away</li>
                </ul>
              </div>
            </div>
          </div>
          <p className="mt-8 text-xs text-zinc-500 max-w-3xl">
            Illustrative comparison based on public industry rates and per-ticket pricing. Actual amounts depend on your ticket price, payment mix and country. Our fee is {Math.round(market.bookingFeePercent * 100)}% of face value per ticket ({fmt(market, market.bookingFeeMinimum, { decimals: 2 })} minimum) — that is the entire cost of running your ticketing on Affra.
          </p>

          {/* Plain-English disclosure: how "0% processing" actually works */}
          <div
            data-testid="about-fees-plain-english"
            className="mt-10 rounded-2xl border border-zinc-200 bg-white p-6 md:p-8 max-w-3xl"
          >
            <div className="text-xs font-mono uppercase tracking-widest text-indigo-600">/ how the maths actually works</div>
            <h3 className="mt-2 font-display font-bold text-2xl tracking-tight">
              We pay the card fees. You keep the ticket.
            </h3>
            <p className="mt-4 text-[15px] text-zinc-700 leading-relaxed">
              Buyers pay a small booking fee at checkout — {Math.round(market.bookingFeePercent * 100)}% of face value with a {fmt(market, market.bookingFeeMinimum, { decimals: 2 })} minimum. That&apos;s how we get paid. Out of that fee, <strong>we cover Stripe&apos;s card processing costs on your behalf</strong> on every ticket you sell. You keep 100% of the face value on every ticket, forever.
            </p>
            <p className="mt-4 text-[15px] text-zinc-700 leading-relaxed">
              No card fees deducted. Nothing to reconcile. Just your ticket price, straight into your Stripe balance.
            </p>
            <p className="mt-4 text-xs text-zinc-500">
              Full plain-English detail is in our{" "}
              <Link to="/terms" className="underline decoration-indigo-600 decoration-2 underline-offset-4 hover:text-zinc-900">Terms of Service</Link>{" "}
              under &ldquo;How Our Fees Work&rdquo;.
            </p>
          </div>
        </div>
      </section>

      {/* MANIFESTO / SIGNED */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-center gap-3 mb-8">
          <span className="w-10 h-10 grid place-items-center rounded-full bg-zinc-900 text-white">
            <PenLine className="w-5 h-5" aria-hidden="true" />
          </span>
          <div className="text-sm font-mono text-indigo-600 uppercase tracking-widest">/ a short manifesto</div>
        </div>

        <div className="prose prose-lg max-w-none">
          <p className="font-display font-black text-3xl lg:text-4xl tracking-tighter leading-[1.1] text-zinc-900">
            We&apos;re not building a middleman. We&apos;re building the pipes.
          </p>
          <p className="mt-8 text-lg text-zinc-700 leading-relaxed">
            The best venues in the country are run by people who care obsessively about the room — the sound, the lineup, the door, the vibe. They shouldn&apos;t also have to be experts in ticketing arbitrage.
          </p>
          <p className="mt-5 text-lg text-zinc-700 leading-relaxed">
            The moment a platform starts making more per ticket than the venue does, something has gone wrong. Not just commercially — <em>morally</em>. Someone with a Slack workspace and a Stripe key is out-earning the people who booked the act, paid the rent and stayed until 3am to lock up.
          </p>
          <p className="mt-5 text-lg text-zinc-700 leading-relaxed">
            So we drew a line. <strong>No monthly fees. No per-ticket cost to venues. Ever.</strong> The buyer pays a small booking fee at checkout — that&apos;s how we get paid, and only when you sell tickets. No percentage. No &ldquo;growth pricing&rdquo;. No &ldquo;enterprise tier&rdquo; that magically appears when you get successful. Your ticket revenue is <strong>yours</strong>. Your customers are <strong>yours</strong>. Your brand is <strong>yours</strong>.
          </p>
          <p className="mt-5 text-lg text-zinc-700 leading-relaxed">
            We&apos;ll make our money the boring way: a small fee on each buyer&apos;s ticket, nothing on you. If we ever stop being useful, you walk. That&apos;s the deal.
          </p>
          <p className="mt-8 font-display font-black text-2xl tracking-tighter text-zinc-900">
            Your Venue. Your Customers. Your Money. Your Call.
          </p>

          <div className="mt-12 pt-8 border-t border-zinc-200 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-zinc-900 text-white grid place-items-center font-display font-black">
              S
            </div>
            <div>
              <div className="font-display font-bold text-lg tracking-tight">The Affra team</div>
              <div className="text-sm text-zinc-500">Built in the UK, on the side of the venue.</div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES STRIP */}
      <section className="border-y border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-3 gap-8">
          <div className="flex items-start gap-4">
            <span className="w-10 h-10 shrink-0 grid place-items-center bg-zinc-900 text-white rounded-lg">
              <Handshake className="w-5 h-5" />
            </span>
            <div>
              <div className="font-display font-bold text-lg tracking-tight">Aligned by design</div>
              <p className="mt-1 text-zinc-600 text-sm leading-relaxed">No monthly fee. No per-ticket cost. No revenue share. If you 10x, we don&apos;t send a bigger invoice — because we never sent one.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <span className="w-10 h-10 shrink-0 grid place-items-center bg-zinc-900 text-white rounded-lg">
              <Lock className="w-5 h-5" />
            </span>
            <div>
              <div className="font-display font-bold text-lg tracking-tight">We never hold your money</div>
              <p className="mt-1 text-zinc-600 text-sm leading-relaxed">Payments go direct to your Stripe. We can&apos;t freeze funds — because we never have them.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <span className="w-10 h-10 shrink-0 grid place-items-center bg-zinc-900 text-white rounded-lg">
              <Sparkles className="w-5 h-5" />
            </span>
            <div>
              <div className="font-display font-bold text-lg tracking-tight">Invisible on purpose</div>
              <p className="mt-1 text-zinc-600 text-sm leading-relaxed">Fully white-label. Your buyers should never see our name — and by default, they don&apos;t.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="rounded-3xl bg-indigo-600 text-white p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -right-16 -bottom-16 w-72 h-72 rounded-full bg-white/10" aria-hidden="true" />
          <div className="text-xs font-mono uppercase tracking-widest text-indigo-200">/ start selling</div>
          <h3 className="mt-3 font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] max-w-3xl">
            Keep 100% of your ticket. Own your buyers. Pay nothing.
          </h3>
          <p className="mt-6 text-indigo-100 max-w-lg text-lg">
            No monthly fees. No per-ticket cost. No lock-in. Live in ten minutes.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link to="/register">
              <Button
                size="lg"
                data-testid="about-cta-register"
                className="rounded-full h-12 px-6 bg-white text-zinc-900 hover:bg-zinc-100 text-base font-semibold"
              >
                Start selling free <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/#pricing" className="inline-flex items-center gap-1 text-white/90 hover:text-white text-sm font-medium">
              See full pricing <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between text-sm text-zinc-500 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Logo size="h-8" />
            <span>© {new Date().getFullYear()} — white-label ticketing.</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/#features" className="hover:text-zinc-900">Features</Link>
            <Link to="/#pricing" className="hover:text-zinc-900">Pricing</Link>
            <Link to="/about" className="hover:text-zinc-900" data-testid="footer-about-link">About</Link>
            <Link to="/#contact" className="hover:text-zinc-900" data-testid="footer-contact-link">Contact</Link>
            <Link to="/privacy" className="hover:text-zinc-900" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/terms" className="hover:text-zinc-900" data-testid="footer-terms-link">Terms</Link>
            <Link to="/login" className="hover:text-zinc-900">Log in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
